/**
 * Phase 3 — Core Positions inside a Department (Person → Position → Role Profile).
 * Multiple occupants per position, optional authorized headcount, optional
 * Role Profile link. Organizational only — never grants RBAC.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Briefcase, Plus, Pencil, Archive, UserPlus, X, Link2 } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';

const NONE = '__none__';

// Novera UAT F1 — defer data refreshes until after the Radix Select/Popper
// finishes its close + measure cycle, so our re-render never mutates layout
// inside the same frame the popper's ResizeObserver is still delivering.
const afterFrame = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

// API `detail` may be a structured object ({code, message, message_es}) —
// always resolve to a localized string before handing it to the toast.
function apiErrMsg(e, isEs) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d && typeof d === 'object') return (isEs && d.message_es) || d.message || 'Failed';
  return 'Failed';
}

export default function DepartmentPositions({ companyId, department, employees = [], onChanged }) {
  const { t, i18n } = useTranslation();
  const isEs = i18n.language?.startsWith('es');
  const [positions, setPositions] = useState([]);
  const [roleProfiles, setRoleProfiles] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', role_profile_id: NONE, headcount: '' });
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    if (!companyId || !department?.id) return;
    try {
      const [pos, rps] = await Promise.all([
        adminApi.listPositions(companyId, department.id),
        adminApi.listRoleProfiles(false, companyId),
      ]);
      setPositions(pos || []);
      setRoleProfiles(rps || []);
    } catch { /* toast handled globally */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [companyId, department?.id]);

  const companyProfiles = useMemo(() => roleProfiles.filter((p) => p.company_id === companyId && !p.archived), [roleProfiles, companyId]);
  const globalProfiles = useMemo(() => roleProfiles.filter((p) => !p.company_id && !p.archived), [roleProfiles]);

  const openNew = () => { setEditing(null); setForm({ title: '', role_profile_id: NONE, headcount: '' }); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({ title: p.title, role_profile_id: p.role_profile_id || NONE, headcount: p.headcount ? String(p.headcount) : '' });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error(t('phase3.positions.titleRequired', { defaultValue: 'Position title is required' })); return; }
    setSaving(true);
    const body = {
      title: form.title.trim(),
      role_profile_id: form.role_profile_id === NONE ? null : form.role_profile_id,
      headcount: form.headcount ? parseInt(form.headcount, 10) : null,
    };
    try {
      if (editing) await adminApi.updatePosition(editing.id, body);
      else await adminApi.createPosition({ ...body, company_id: companyId, department_id: department.id });
      toast.success(t('common.saved', { defaultValue: 'Saved' }));
      setDialogOpen(false);
      reload();
    } catch (e) { toast.error(apiErrMsg(e, isEs)); }
    finally { setSaving(false); }
  };

  const archivePosition = async (p) => {
    try {
      await adminApi.updatePosition(p.id, { archived: true });
      toast.success(t('common.archived', { defaultValue: 'Archived' }));
      reload();
    } catch (e) { toast.error(apiErrMsg(e, isEs)); }
  };

  const assign = async (p, userId) => {
    try {
      await adminApi.assignPositionOccupant(p.id, userId);
      toast.success(t('phase3.positions.occupantAssigned', { defaultValue: 'Occupant assigned' }));
      afterFrame(() => { reload(); onChanged?.(); });
    } catch (e) { toast.error(apiErrMsg(e, isEs)); }
  };

  const removeOcc = async (p, userId) => {
    try {
      await adminApi.removePositionOccupant(p.id, userId);
      toast.success(t('phase3.positions.occupantRemoved', { defaultValue: 'Occupant removed (history preserved)' }));
      afterFrame(() => { reload(); onChanged?.(); });
    } catch (e) { toast.error(apiErrMsg(e, isEs)); }
  };

  const occupantIds = useMemo(() => new Set(positions.flatMap((p) => (p.occupants || []).map((o) => o.id))), [positions]);

  // Founder reconciliation sprint — collaborators of THIS department not yet
  // linked to a governed Formal Position (users.position_id). Informational
  // setup state, never auto-converted from free-text job titles.
  const deptMembers = useMemo(
    () => (employees || []).filter((e) => e.department_id === department?.id),
    [employees, department?.id],
  );
  const unlinked = useMemo(
    () => deptMembers.filter((e) => !e.position_id && !occupantIds.has(e.id)),
    [deptMembers, occupantIds],
  );

  // Reconcile dialog state
  const [recTarget, setRecTarget] = useState(null); // collaborator being linked
  const [recMode, setRecMode] = useState('existing'); // existing | create
  const [recPositionId, setRecPositionId] = useState('');
  const [recForm, setRecForm] = useState({ title: '', role_profile_id: NONE, headcount: '1', assign: true });
  const [recSaving, setRecSaving] = useState(false);

  const openReconcile = (member) => {
    setRecTarget(member);
    setRecMode(positions.length ? 'existing' : 'create');
    setRecPositionId('');
    setRecForm({ title: member.position || '', role_profile_id: NONE, headcount: '1', assign: true });
  };

  const confirmReconcile = async () => {
    if (!recTarget) return;
    setRecSaving(true);
    try {
      if (recMode === 'existing') {
        if (!recPositionId) {
          toast.error(t('phase3.reconcile.pickPosition', { defaultValue: 'Select a formal position first' }));
          return;
        }
        await adminApi.assignPositionOccupant(recPositionId, recTarget.id);
        toast.success(t('phase3.positions.occupantAssigned', { defaultValue: 'Occupant assigned' }));
      } else {
        if (!recForm.title.trim()) {
          toast.error(t('phase3.positions.titleRequired', { defaultValue: 'Position title is required' }));
          return;
        }
        const created = await adminApi.createPosition({
          title: recForm.title.trim(),
          role_profile_id: recForm.role_profile_id === NONE ? null : recForm.role_profile_id,
          headcount: recForm.headcount ? parseInt(recForm.headcount, 10) : null,
          company_id: companyId,
          department_id: department.id,
        });
        const pid = created?.id || created?.position?.id;
        if (recForm.assign && pid) await adminApi.assignPositionOccupant(pid, recTarget.id);
        toast.success(t('phase3.reconcile.created', { defaultValue: 'Formal position created' }));
      }
      setRecTarget(null);
      afterFrame(() => { reload(); onChanged?.(); });
    } catch (e) { toast.error(apiErrMsg(e, isEs)); }
    finally { setRecSaving(false); }
  };

  return (
    <div className="mt-6 pt-5 border-t border-border" data-testid="dept-positions-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="font-display font-bold text-base flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-primary" />
            {t('phase3.positions.sectionTitle', { defaultValue: 'Positions' })}
          </h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t('phase3.positions.sectionHint', { defaultValue: 'Each position may link a Role Profile and hold multiple occupants. Organizational only — no permissions are granted.' })}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={openNew} data-testid="position-new-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" />{t('phase3.positions.new', { defaultValue: 'New position' })}
        </Button>
      </div>

      {/* Founder reconciliation sprint — setup signal, not an error state. */}
      {unlinked.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3.5" data-testid="reconcile-block">
          <p className="text-sm font-medium text-amber-900 flex items-center gap-1.5">
            <UserPlus className="h-4 w-4" />
            {t('phase3.reconcile.pending', {
              count: unlinked.length,
              defaultValue: '{{count}} collaborator(s) without a linked formal position',
            })}
          </p>
          <p className="text-xs text-amber-800/80 mt-0.5">
            {t('phase3.reconcile.hint', { defaultValue: 'Link each collaborator to an existing formal position or create a new one from their current job title. Nothing is converted automatically.' })}
          </p>
          <ul className="mt-2.5 space-y-1.5">
            {unlinked.map((m) => (
              <li key={m.id} className="flex items-center justify-between gap-2 flex-wrap rounded-md bg-white/70 border border-amber-100 px-2.5 py-1.5"
                  data-testid={`reconcile-row-${m.id}`}>
                <div className="min-w-0">
                  <span className="text-sm font-medium">{m.name}</span>
                  {m.position && <span className="text-xs text-muted-foreground ml-2">· {m.position}</span>}
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0"
                        onClick={() => openReconcile(m)} data-testid={`reconcile-link-${m.id}`}>
                  <Link2 className="h-3 w-3 mr-1" />
                  {t('phase3.reconcile.linkAction', { defaultValue: 'Link to formal position' })}
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!positions.length ? (
        <p className="text-sm text-muted-foreground italic py-4 text-center" data-testid="positions-empty">
          {t('phase3.positions.empty', { defaultValue: 'No positions defined in this department yet.' })}
        </p>
      ) : (
        <div className="space-y-3">
          {positions.map((p) => {
            const seats = p.headcount || null;
            const vacant = seats ? Math.max(0, seats - p.occupied) : null;
            return (
              <div key={p.id} className="border border-border rounded-lg p-3.5" data-testid={`position-row-${p.id}`}>
                {/* Founder layout fix — badges/occupants/actions stack in rows
                    and wrap; metrics never collide with the collaborator block. */}
                <div className="flex items-start justify-between flex-wrap gap-x-3 gap-y-2">
                  <div className="min-w-0 flex-1 basis-64">
                    <div className="flex items-center gap-x-2 gap-y-1.5 flex-wrap">
                      <span className="font-semibold text-sm">{p.title}</span>
                      {p.role_profile ? (
                        <Badge variant="outline" className="text-xs gap-1" data-testid={`position-rp-${p.id}`}>
                          <Link2 className="h-3 w-3" />{p.role_profile.position_title}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          {t('phase3.positions.noProfile', { defaultValue: 'No role profile' })}
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs tabular-nums" data-testid={`position-seats-${p.id}`}>
                        {seats
                          ? t('phase3.positions.seats', { defaultValue: '{{occupied}}/{{total}} seats', occupied: p.occupied, total: seats })
                          : `${p.occupied} ${t('phase3.positions.occupantsLabel', { defaultValue: 'occupant(s)' })}`}
                      </Badge>
                      {seats && vacant > 0 && (
                        <Badge className="bg-warning/10 text-warning border-warning/30 text-xs" variant="outline">
                          {vacant} {t('phase3.positions.vacant', { defaultValue: 'vacant' })}
                        </Badge>
                      )}
                      {p.occupied === 0 && !seats && (
                        <Badge className="bg-warning/10 text-warning border-warning/30 text-xs" variant="outline">
                          {t('phase3.positions.vacantPosition', { defaultValue: 'Vacant' })}
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {(p.occupants || []).map((o) => (
                        <Badge key={o.id} variant="outline" className="text-xs gap-1 pr-1" data-testid={`position-occ-${p.id}-${o.id}`}>
                          {o.name}
                          <button
                            type="button"
                            className="ml-0.5 rounded-full hover:bg-destructive/10 hover:text-destructive p-0.5"
                            onClick={() => removeOcc(p, o.id)}
                            data-testid={`position-occ-remove-${p.id}-${o.id}`}
                            title={t('phase3.positions.removeOccupant', { defaultValue: 'Remove occupant (history preserved)' })}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap w-full lg:w-auto lg:shrink-0">
                    <Select value="" onValueChange={(v) => assign(p, v)}>
                      <SelectTrigger className="h-8 text-xs w-48" data-testid={`position-assign-${p.id}`}>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <UserPlus className="h-3.5 w-3.5" />
                          {t('phase3.positions.assignOccupant', { defaultValue: 'Assign occupant' })}
                        </span>
                      </SelectTrigger>
                      <SelectContent>
                        {employees.filter((e) => !(p.occupants || []).some((o) => o.id === e.id)).map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.name}{occupantIds.has(e.id) ? ` · ${t('phase3.positions.hasPosition', { defaultValue: 'has position' })}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => openEdit(p)} data-testid={`position-edit-${p.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground" onClick={() => archivePosition(p)} data-testid={`position-archive-${p.id}`}>
                      <Archive className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reconcile dialog — Option A: existing position · Option B: create
          new (pre-filled from current job title, explicit confirmation). */}
      <Dialog open={!!recTarget} onOpenChange={(o) => !o && setRecTarget(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="reconcile-dialog">
          <DialogHeader>
            <DialogTitle>{t('phase3.reconcile.dialogTitle', { defaultValue: 'Link to formal position' })}</DialogTitle>
            <DialogDescription>
              {recTarget && t('phase3.reconcile.dialogDesc', {
                name: recTarget.name,
                defaultValue: '{{name}} — assign to an available seat in an existing formal position, or create a new governed position. The role profile link stays a separate, explicit step.',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setRecMode('existing')} disabled={!positions.length}
                      className={`rounded-lg border p-3 text-left text-sm transition ${recMode === 'existing' ? 'border-primary ring-1 ring-primary/40 bg-primary/5' : 'hover:bg-muted/40'} ${!positions.length ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="reconcile-mode-existing">
                <p className="font-semibold">{t('phase3.reconcile.optExisting', { defaultValue: 'Existing position' })}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {positions.length
                    ? t('phase3.reconcile.optExistingHint', { defaultValue: 'Assign to an available seat.' })
                    : t('phase3.reconcile.noPositions', { defaultValue: 'No positions in this department yet.' })}
                </p>
              </button>
              <button type="button" onClick={() => setRecMode('create')}
                      className={`rounded-lg border p-3 text-left text-sm transition ${recMode === 'create' ? 'border-primary ring-1 ring-primary/40 bg-primary/5' : 'hover:bg-muted/40'}`}
                      data-testid="reconcile-mode-create">
                <p className="font-semibold">{t('phase3.reconcile.optCreate', { defaultValue: 'New formal position' })}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('phase3.reconcile.optCreateHint', { defaultValue: 'Pre-filled from the current job title — you confirm every field.' })}
                </p>
              </button>
            </div>

            {recMode === 'existing' ? (
              <div>
                <Label>{t('phase3.reconcile.positionLabel', { defaultValue: 'Formal position' })}</Label>
                <Select value={recPositionId} onValueChange={setRecPositionId}>
                  <SelectTrigger className="mt-1.5" data-testid="reconcile-position-select">
                    <SelectValue placeholder={t('phase3.reconcile.pickPosition', { defaultValue: 'Select a formal position first' })} />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((p) => {
                      const free = p.headcount ? Math.max(0, p.headcount - p.occupied) : null;
                      return (
                        <SelectItem key={p.id} value={p.id}>
                          {p.title}
                          {free != null
                            ? ` · ${free} ${t('phase3.reconcile.freeSeats', { defaultValue: 'free seat(s)' })}`
                            : ` · ${p.occupied} ${t('phase3.positions.occupantsLabel', { defaultValue: 'occupant(s)' })}`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                <div>
                  <Label>{t('phase3.positions.titleLabel', { defaultValue: 'Position title' })}</Label>
                  <Input className="mt-1.5" value={recForm.title}
                         onChange={(e) => setRecForm({ ...recForm, title: e.target.value })}
                         data-testid="reconcile-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('phase3.positions.headcountLabel', { defaultValue: 'Authorized headcount (optional)' })}</Label>
                    <Input className="mt-1.5" type="number" min={1} value={recForm.headcount}
                           onChange={(e) => setRecForm({ ...recForm, headcount: e.target.value })}
                           data-testid="reconcile-headcount-input" />
                  </div>
                  <div>
                    <Label>{t('phase3.positions.roleProfileLabel', { defaultValue: 'Role profile (optional)' })}</Label>
                    <Select value={recForm.role_profile_id} onValueChange={(v) => setRecForm({ ...recForm, role_profile_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="reconcile-rp-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>{t('phase3.positions.noProfile', { defaultValue: 'No role profile' })}</SelectItem>
                        {companyProfiles.map((rp) => <SelectItem key={rp.id} value={rp.id}>{rp.position_title}</SelectItem>)}
                        {globalProfiles.map((rp) => <SelectItem key={rp.id} value={rp.id}>{rp.position_title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="reconcile-assign-toggle">
                  <input type="checkbox" checked={recForm.assign}
                         onChange={(e) => setRecForm({ ...recForm, assign: e.target.checked })} />
                  {recTarget && t('phase3.reconcile.assignAfter', { name: recTarget.name, defaultValue: 'Assign {{name}} as occupant after creating' })}
                </label>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecTarget(null)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={confirmReconcile} disabled={recSaving} data-testid="reconcile-confirm-btn">
              {recSaving ? t('common.saving', { defaultValue: 'Saving…' })
                : recMode === 'existing'
                  ? t('phase3.reconcile.confirmAssign', { defaultValue: 'Assign to seat' })
                  : t('phase3.reconcile.confirmCreate', { defaultValue: 'Create position' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="position-dialog">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t('phase3.positions.editTitle', { defaultValue: 'Edit position' })
                : t('phase3.positions.newTitle', { defaultValue: 'New position' })}
            </DialogTitle>
            <DialogDescription>
              {t('phase3.positions.dialogDesc', { defaultValue: 'A position belongs to this department and may reference a Role Profile. Occupants are assigned explicitly — never by name matching.' })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('phase3.positions.titleLabel', { defaultValue: 'Position title' })}</Label>
              <Input className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="position-title-input" />
            </div>
            <div>
              <Label>{t('phase3.positions.roleProfileLabel', { defaultValue: 'Role profile (optional)' })}</Label>
              <Select value={form.role_profile_id} onValueChange={(v) => setForm({ ...form, role_profile_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="position-rp-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('phase3.positions.noProfile', { defaultValue: 'No role profile' })}</SelectItem>
                  {companyProfiles.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>{t('phase3.positions.companyProfiles', { defaultValue: 'Company profiles' })}</SelectLabel>
                      {companyProfiles.map((rp) => <SelectItem key={rp.id} value={rp.id}>{rp.position_title}</SelectItem>)}
                    </SelectGroup>
                  )}
                  {globalProfiles.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>{t('phase3.positions.globalProfiles', { defaultValue: 'Global CTH templates' })}</SelectLabel>
                      {globalProfiles.map((rp) => <SelectItem key={rp.id} value={rp.id}>{rp.position_title}</SelectItem>)}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('phase3.positions.headcountLabel', { defaultValue: 'Authorized headcount (optional)' })}</Label>
              <Input className="mt-1.5 w-40" type="number" min={1} value={form.headcount}
                     onChange={(e) => setForm({ ...form, headcount: e.target.value })}
                     placeholder={t('phase3.positions.headcountPh', { defaultValue: 'e.g. 5' })}
                     data-testid="position-headcount-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
            <Button onClick={save} disabled={saving} data-testid="position-save-btn">
              {saving ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save', { defaultValue: 'Save' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
